import { ApplicationState, AppState, AppThunkAction } from '.././';
import * as ActionTypes from './ActionTypes';
import { AppContext } from '../../context/Contexts';
import { AppUser } from '../../interfaces/AppUser';
import { Language, Organization } from '../../interfaces/Organization';
import { CurrencyRate } from '../../interfaces/Vehicle';
import { Constants, RoleClaimsEnum } from '../../helpers/Constants';
import { EventSource, EventSourceInit } from 'eventsource';
import {
  NotificationType,
  UserNotification,
  UserNotificationsUpdate
} from '../../interfaces/UserNotificationsUpdate';
import { IRefreshService } from '../../services/Interfaces/IRefreshService';
import { decodeJwtPayload } from '../../hooks/useRefreshToken';
import { captureAuthError } from '../../helpers/Sentry';
import { hasRight } from '../../utils/general';

interface AppSetUserAction {
  type: typeof ActionTypes.APP_SET_USER;
  user: any;
  appUser: AppUser;
}

interface AppRemoveUserAction {
  type: typeof ActionTypes.APP_REMOVE_USER;
}

interface AppDisplayMessageAction {
  type: typeof ActionTypes.APP_DISPLAY_MESSAGE;
  display: boolean;
}

interface AppSetLanguageAction {
  type: typeof ActionTypes.APP_SET_LANGUAGE;
  language: string;
  dateFormat: string;
  longDateFormat: string;
}

interface AppLoadUserNotifications {
  type: typeof ActionTypes.APP_LOAD_NOTIFICATION;
  userNotifications: UserNotification[];
}

interface AppRemoveNotification {
  type: typeof ActionTypes.APP_REMOVE_NOTIFICATION;
  id: number;
}

interface AppSetCurrencyRates {
  type: typeof ActionTypes.APP_SET_CURRENCY_RATES;
  currencyRates: CurrencyRate[];
}

interface AppMarkAsViewedNotification {
  type: typeof ActionTypes.APP_MARK_AS_VIEWED_NOTIFICATION;
  id: number;
}

interface AppSetTokenAction {
  type: typeof ActionTypes.APP_SET_TOKEN;
  token: string | null;
}

interface AppSetRefreshTokenAction {
  type: typeof ActionTypes.APP_SET_REFRESH_TOKEN;
  refreshToken: string | null;
}

interface AppSetUserOrganizationsAction {
  type: typeof ActionTypes.APP_SET_USER_ORGANIZATIONS;
  userOrganizations: Organization[];
}

interface AppSetGeneralLoadingAction {
  type: typeof ActionTypes.APP_SET_GENERAL_LOADING;
  isGeneralLoading: boolean;
}

export type AppAction =
  | AppSetUserAction
  | AppRemoveUserAction
  | AppDisplayMessageAction
  | AppSetLanguageAction
  | AppLoadUserNotifications
  | AppRemoveNotification
  | AppSetCurrencyRates
  | AppMarkAsViewedNotification
  | AppSetTokenAction
  | AppSetRefreshTokenAction
  | AppSetUserOrganizationsAction
  | AppSetGeneralLoadingAction;

export const AppActionCreators = {
  SetUser:
    (enqueueSnackbar: any): AppThunkAction<AppAction> =>
    async (dispatch, getState, context) => {
      const appUserService = (context as AppContext).appUserService;
      const translatorService = (context as AppContext).translatorService;
      const organizationService = (context as AppContext).organizationService;

      const accessToken = localStorage.getItem('access_token');
      if (!accessToken) return;

      const decoded = decodeJwtPayload(accessToken);
      if (!decoded) return;

      const tokenUser = {
        access_token: accessToken,
        profile: {
          role: decoded.role || [],
          name: decoded.name || decoded.sub || '',
          organization_id: decoded.organization_id || null
        }
      };

      // Show the loader while profile + user list load.
      dispatch({ type: ActionTypes.APP_SET_GENERAL_LOADING, isGeneralLoading: true });
      try {
        await translatorService.GetLanguages();

        const isAdmin = hasRight(tokenUser, RoleClaimsEnum.Admin);

        const [appUser, userOrganizations] = await Promise.all([
          appUserService.GetProfile(),
          isAdmin ? organizationService.GetOrganizationsByIds([]) : []
        ]);

        dispatch({ type: ActionTypes.APP_SET_USER, user: tokenUser, appUser: appUser });
        dispatch({
          type: ActionTypes.APP_SET_USER_ORGANIZATIONS,
          userOrganizations: userOrganizations ?? []
        });

        const languageCode = translatorService.GetLanguage();
        const language = appUser.organization!.gtOrganization!.languages!.find(
          (item) => item.code === languageCode
        ) as Language;

        dispatch({
          type: ActionTypes.APP_SET_LANGUAGE,
          language: languageCode,
          dateFormat: language ? language.dateFormat : 'DD.MM.YYYY',
          longDateFormat: language ? language.longDateFormat : 'DD.MM.YYYY HH:mm'
        });

        createNotificationsEventSource(getState, context, dispatch);
      } catch (error) {
        console.log(error);
        captureAuthError('Bootstrap Current User', error);
        enqueueSnackbar(
          translatorService.Tranlate('ERROR_INACTIVE_USER_MSG', 'Utilizator inactiv!'),
          {
            variant: 'error',
            autoHideDuration: 6000
          }
        );
      } finally {
        dispatch({ type: ActionTypes.APP_SET_GENERAL_LOADING, isGeneralLoading: false });
      }
    },
  RemoveUser: (): AppThunkAction<AppAction> => async (dispatch, getState, context) => {
    dispatch({ type: ActionTypes.APP_REMOVE_USER });
  },
  Message:
    (isDisplayed: boolean): AppThunkAction<AppAction> =>
    async (dispatch, getState, context) => {
      dispatch({ type: ActionTypes.APP_DISPLAY_MESSAGE, display: isDisplayed });
    },
  SetLanguage: (): AppThunkAction<AppAction> => async (dispatch, getState, context) => {
    const translatorService = (context as AppContext).translatorService;
    const languageCode = translatorService.GetLanguage();

    const language = getState().app!.appUser!.organization!.gtOrganization!.languages!.find(
      (item) => item.code === languageCode
    ) as Language;

    translatorService.SetLanguage(language.code);

    dispatch({
      type: ActionTypes.APP_SET_LANGUAGE,
      language: languageCode,
      dateFormat: language.dateFormat,
      longDateFormat: language.longDateFormat
    });
  },

  RemoveNotification:
    (id: number): AppThunkAction<AppAction> =>
    async (dispatch, getState, context) => {
      dispatch({
        type: ActionTypes.APP_REMOVE_NOTIFICATION,
        id: id
      });
    },
  MarkAsViewedNotification:
    (id: number): AppThunkAction<AppAction> =>
    async (dispatch, getState, context) => {
      dispatch({
        type: ActionTypes.APP_MARK_AS_VIEWED_NOTIFICATION,
        id: id
      });
    },
  LoadCurrencyRates:
    (organization: Organization | null | undefined): AppThunkAction<AppAction> =>
    async (dispatch, getState, context) => {
      const vehicleService = (context as AppContext).vehicleService;

      const rates = await vehicleService.GetCurrencyRates(organization, false);

      dispatch({
        type: ActionTypes.APP_SET_CURRENCY_RATES,
        currencyRates: rates
      });
    },

  SetToken:
    (token: string | null): AppThunkAction<AppAction> =>
    async (dispatch) => {
      if (token) {
        localStorage.setItem('access_token', token);
      } else {
        localStorage.removeItem('access_token');
      }
      dispatch({ type: ActionTypes.APP_SET_TOKEN, token });
    },

  SetRefreshToken:
    (refreshToken: string | null): AppThunkAction<AppAction> =>
    async (dispatch) => {
      if (refreshToken) {
        localStorage.setItem('refresh_token', refreshToken);
      } else {
        localStorage.removeItem('refresh_token');
      }
      dispatch({ type: ActionTypes.APP_SET_REFRESH_TOKEN, refreshToken });
    },

  LogoutCredentials: (): AppThunkAction<AppAction> => async (dispatch) => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');

    sessionStorage.removeItem('safari_auth_token');
    sessionStorage.removeItem('safari_auth_refreshToken');
    sessionStorage.removeItem('safari_auth_user');

    dispatch({ type: ActionTypes.APP_SET_TOKEN, token: null });
    dispatch({ type: ActionTypes.APP_SET_REFRESH_TOKEN, refreshToken: null });
    dispatch({ type: ActionTypes.APP_REMOVE_USER });
    dispatch({ type: ActionTypes.APP_LOAD_NOTIFICATION, userNotifications: [] });
    dispatch({ type: ActionTypes.APP_SET_CURRENCY_RATES, currencyRates: [] });
    dispatch({ type: ActionTypes.APP_SET_USER_ORGANIZATIONS, userOrganizations: [] });
  },

  SetGeneralLoading:
    (isGeneralLoading: boolean): AppThunkAction<AppAction> =>
    async (dispatch) => {
      dispatch({ type: ActionTypes.APP_SET_GENERAL_LOADING, isGeneralLoading });
    }
};

function createNotificationsEventSource(
  getState: () => ApplicationState,
  context: AppContext,
  dispatch: (action: AppAction) => void
) {
  const notificationsUrl = `${Constants.apiRoot}case/notificationsStream`;
  const access_token = localStorage.getItem('access_token');
  const locale = localStorage.getItem('locale');
  const appState = getState().app;
  if (!appState) {
    return;
  }
  const eventSourceOptions: EventSourceInit = {
    fetch: (input, init) =>
      fetch(input, {
        ...init,
        headers: init
          ? {
              ...init.headers,
              Authorization: 'Bearer ' + access_token,
              Locale: locale ? locale : 'ro'
            }
          : {
              Authorization: 'Bearer ' + access_token,
              Locale: locale ? locale : 'ro'
            }
      })
  };
  const notificationsEventSource = new EventSource(notificationsUrl, eventSourceOptions);
  const refreshService = context.refreshService;

  notificationsEventSource.onmessage = async function (
    this: {
      refreshService: IRefreshService;
      appState: () => ApplicationState;
      dispatch: (action: AppAction) => void;
    },
    ev: MessageEvent
  ) {
    if (ev.data === '"heartbeat"') {
      return;
    }

    let userNotifications = this.appState().app?.userNotifications;

    const notifications = JSON.parse(ev.data) as UserNotificationsUpdate;

    if (notifications.casesToUpdate && notifications.casesToUpdate.length > 0) {
      refreshService.RefreshCaseList();
      refreshService.SetCasesToRefresh(notifications.casesToUpdate);
    }

    if (notifications.appointmentsToUpdate && notifications.appointmentsToUpdate.length > 0) {
      refreshService.SetAppointmentsToRefresh(notifications.appointmentsToUpdate);
    }

    if (notifications.updateAdminAuctionGrid) {
      this.refreshService.RefreshAdminAuctionGrid();
    }

    if (notifications.activeAuctionsToUpdate?.length > 0) {
      this.refreshService.RefreshAuctions();
    }

    if (notifications.myAuctionsToUpdate?.length > 0) {
      this.refreshService.RefreshMyAuctions();
    }

    if (notifications.userNotifications) {
      userNotifications = notifications.userNotifications;

      const supplierUserNotifications = notifications.userNotifications.filter(
        (notification: UserNotification) => {
          return notification.type === NotificationType.Supplier;
        }
      );

      if (supplierUserNotifications.length > 0) {
        this.refreshService.RefreshOrderList();
      }

      const apptUserNotifications = notifications.userNotifications.filter(
        (notification: UserNotification) => {
          return notification.type === NotificationType.Appointment;
        }
      );

      if (apptUserNotifications.length > 0) {
        this.refreshService.RefreshAppointments();
      }
    }

    if (notifications.ordersToUpdate) {
      this.refreshService.SetOrdersToRefresh(notifications.ordersToUpdate);
    }

    dispatch({
      type: ActionTypes.APP_LOAD_NOTIFICATION,
      userNotifications: userNotifications ? userNotifications : []
    });
  }.bind({ refreshService: refreshService, appState: getState, dispatch: dispatch });
}
